import React, { useMemo, useState } from 'react';
import { Search, Check } from 'lucide-react';
import { filterCandidates } from './userPickerFilter';

/**
 * Pick one user from a long list: a search box over a fixed-height list that
 * scrolls on its own, so the modal never grows past the screen.
 */
const UserPicker = ({ candidates, value, onChange }) => {
  const [query, setQuery] = useState('');
  const shown = useMemo(() => filterCandidates(candidates, query), [candidates, query]);
  const selected = (candidates || []).find((u) => String(u.id) === String(value));

  return (
    <div className="border border-gray-300 rounded-lg bg-white">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <Search size={14} className="text-gray-400 shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, phone or vertical"
          className="w-full text-sm outline-none"
          aria-label="Search users"
          autoFocus
        />
        <span className="text-xs text-gray-400 shrink-0">
          {shown.length}/{(candidates || []).length}
        </span>
      </div>

      <ul className="max-h-56 overflow-y-auto overscroll-contain py-1" role="listbox">
        {shown.length === 0 ? (
          <li className="px-3 py-3 text-sm text-gray-500">No user matches “{query}”.</li>
        ) : (
          shown.map((user) => {
            const active = String(user.id) === String(value);
            return (
              <li key={user.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => onChange(String(user.id))}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    active ? 'bg-[#2A565E]/10' : ''
                  }`}>
                  <span className="min-w-0">
                    <span className="block font-medium text-gray-900 truncate">{user.name}</span>
                    <span className="block text-xs text-gray-500 truncate">
                      {[user.designation?.name || user.vertical, user.email || user.phone_number]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {active && <Check size={16} className="text-[#2A565E] shrink-0" />}
                </button>
              </li>
            );
          })
        )}
      </ul>

      {selected && (
        <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-600">
          Selected: <span className="font-medium text-gray-900">{selected.name}</span>
        </div>
      )}
    </div>
  );
};

export default UserPicker;
